import { useAuth } from '../context/AuthContext';
import ProductCustomization from '../components/ProductCustomization';
import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate, useLocation } from 'react-router-dom';
import { ShoppingCart, ArrowLeft, Check, Minus, Plus, Upload, ChevronRight, ChevronDown, Ruler, X, GraduationCap } from 'lucide-react';
import axios from 'axios';
import { useCart } from '../context/CartContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ProductDetailPage = ({ preview = false }) => {
  const { token } = useAuth();
  const { slug, id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const schoolContext = location.state?.schoolName || null;
  const { addToCart, loading: cartLoading } = useCart();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(0);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [customText, setCustomText] = useState('');
  const [addedToCart, setAddedToCart] = useState(false);
  const [sizingCharts, setSizingCharts] = useState([]);
  const [chartModalOpen, setChartModalOpen] = useState(false);
  const [decoration, setDecoration] = useState({ placements: [] });
  const [customPrice, setCustomPrice] = useState(null);
  const [priceError, setPriceError] = useState('');
  const [addError, setAddError] = useState('');
  const [productSelections, setProductSelections] = useState({});
  const [bundleSelections, setBundleSelections] = useState([]);  // [{ key, product_id, fit, size, color }]

  useEffect(() => {
    fetchProduct();
  }, [slug, id, preview, token]);

  const fetchProduct = async () => {
    setLoading(true);
    try {
      const res = await axios.get(preview ? `${API_URL}/api/products/admin/${id}` : `${API_URL}/api/products/${slug}`, preview ? { headers: { Authorization: `Bearer ${token}` } } : {});
      setProduct(res.data);
      if (res.data.variants?.length > 0) {
        setSelectedVariant(res.data.variants[0]);
      } else {
        setSelectedVariant(null);
      }
      setCustomText(''); setQuantity(1); setSelectedImage(0);
      setProductSelections({});
      setDecoration({ placements: [] }); setCustomPrice(null);
      setBundleSelections([]);
      // Pull sizing charts for the school if this is a school product.
      if (res.data.school_id) {
        try {
          const sc = await axios.get(`${API_URL}/api/sizing-charts/school/${res.data.school_id}`);
          setSizingCharts(sc.data || []);
        } catch (e) { /* non-fatal */ }
      } else {
        setSizingCharts([]);
      }
    } catch (err) {
      console.error('Failed to fetch product:', err);
    } finally {
      setLoading(false);
    }
  };

  const isCustom = product?.product_kind === 'custom_apparel';
  const pricingKey = JSON.stringify({ product: product?.id, variant: selectedVariant?.id, quantity, productSelections, decoration });
  useEffect(() => {
    if (!isCustom) return;
    let active = true;
    setCustomPrice(null); setPriceError('');
    const timer = setTimeout(async () => {
      try {
        const { data } = await axios.post(`${API_URL}/api/products/${product.id}/price`, {
          variant_id: selectedVariant?.id || null, quantity, options: { ...productSelections, decoration },
        }, preview ? { headers: { Authorization: `Bearer ${token}` } } : {});
        if (active) setCustomPrice(data);
      } catch (e) { if (active) setPriceError(typeof e.response?.data?.detail === 'string' ? e.response.data.detail : 'Complete your selections to calculate the price.'); }
    }, 250);
    return () => { active = false; clearTimeout(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pricingKey, isCustom, preview, token]);

  const isBundle = product && (product.product_kind || '') === 'school_uniform_bundle';
  const productFits = product?.fits || [];
  const productSizes = product?.sizes || [];
  const productColors = product?.colors || [];
  const usesStructuredOptions = !isBundle && !selectedVariant && (
    productFits.length > 0 || productSizes.length > 0 || productColors.length > 0
  );
  const productOptionsReady = !usesStructuredOptions || (
    (productFits.length === 0 || !!productSelections.fit) &&
    (productSizes.length === 0 || !!productSelections.size) &&
    (productColors.length === 0 || !!productSelections.color)
  );

  // School name eligibility and its price come from the same server rule.
  const isSchoolProduct = product && (
    !!product.school_id || (product.product_kind || '').startsWith('school_uniform')
  );
  const showCustomization = product && product.requires_customization && !isBundle && (
    !isSchoolProduct || product.name_personalization_available
  );

  // Compute expected bundle slots (one per shirt/hoodie/etc × parent qty).
  const bundleSlots = React.useMemo(() => {
    if (!isBundle) return [];
    const out = [];
    (product.bundle_items || []).forEach((bi) => {
      const perBundleQty = bi.quantity || 1;
      for (let b = 0; b < (quantity || 1); b += 1) {
        for (let i = 0; i < perBundleQty; i += 1) {
          out.push({
            key: `${bi.product_id}-${b}-${i}`,
            product_id: bi.product_id,
            name: bi.name,
            fits: bi.fits || [],
            sizes: bi.sizes || [],
            colors: bi.colors || [],
            bundle_index: b,
          });
        }
      }
    });
    return out;
  }, [isBundle, product, quantity]);

  const getSlotSel = (key) => bundleSelections.find((s) => s.key === key) || {};
  const setSlotSel = (key, patch) => {
    setBundleSelections((prev) => {
      const next = [...prev];
      const idx = next.findIndex((s) => s.key === key);
      if (idx >= 0) next[idx] = { ...next[idx], ...patch };
      else next.push({ key, ...patch });
      return next;
    });
  };

  const bundleReady = !isBundle || bundleSlots.every((slot) => {
    const sel = getSlotSel(slot.key);
    return (
      (slot.fits.length === 0 || !!sel.fit) &&
      (slot.sizes.length === 0 || !!sel.size) &&
      (slot.colors.length === 0 || !!sel.color)
    );
  });

  const handleAddToCart = async () => {
    if (preview || !product || (isCustom && !customPrice)) return;
    setAddError('');
    if (!productOptionsReady || (isBundle && !bundleReady)) return;
    // Prefix school context (from BackToSchool "Other Schools" flow) into the
    // customization text so it flows through cart → order → HighLevel notes.
    const parts = [];
    if (schoolContext) parts.push(`School: ${schoolContext}`);
    if (customText) parts.push(customText);
    const customOptions = { text: parts.length ? parts.join(' — ') : undefined };
    if (schoolContext || usesStructuredOptions) {
      customOptions.options = {
        ...(schoolContext ? { school_name: schoolContext } : {}),
        ...(usesStructuredOptions ? {
          fit: productSelections.fit,
          size: productSelections.size,
          color: productSelections.color,
          garment_type: product.garment_type,
          product_kind: product.product_kind,
        } : {}),
      };
    }
    if (isBundle) {
      customOptions.options = {
        ...(customOptions.options || {}),
        bundle_selections: {
          item_selections: bundleSlots.map((slot) => ({
            key: slot.key,
            product_id: slot.product_id,
            product_name: slot.name,
            bundle_index: slot.bundle_index,
            fit: getSlotSel(slot.key).fit,
            size: getSlotSel(slot.key).size,
            color: getSlotSel(slot.key).color,
          })),
        },
      };
    }
    if (isCustom) customOptions.options = customPrice.options;
    const result = await addToCart(
      product.id,
      selectedVariant?.id || null,
      quantity,
      customOptions,
    );
    if (!result.success) setAddError(result.error || 'Could not add this item. Please try again.');
    if (result.success) {
      setAddedToCart(true);
      setTimeout(() => setAddedToCart(false), 2500);
    }
  };

  const currentPrice = () => {
    if (isCustom && customPrice) return customPrice.unit_price;
    let price = product.sale_price ?? product.base_price;
    if (selectedVariant) price += selectedVariant.price_adjustment;
    if (customText.trim()) price += product.name_personalization_fee || 0;
    return price;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white" data-testid="product-detail-loading">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid md:grid-cols-2 gap-12">
            <div className="aspect-square bg-gray-100 rounded-2xl animate-pulse" />
            <div className="space-y-4">
              <div className="h-8 bg-gray-100 rounded w-3/4 animate-pulse" />
              <div className="h-6 bg-gray-100 rounded w-1/4 animate-pulse" />
              <div className="h-24 bg-gray-100 rounded animate-pulse" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center" data-testid="product-not-found">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Product Not Found</h1>
          <Link to="/back-to-school" className="text-[#4A90A4] hover:underline">Back to Schools</Link>
        </div>
      </div>
    );
  }

  const images = product.images?.length > 0
    ? product.images.sort((a, b) => a.sort_order - b.sort_order)
    : [{ url: null, alt_text: product.name }];

  return (
    <div className="min-h-screen bg-white" data-testid="product-detail-page">
      {preview && <div className="bg-amber-50 border-b border-amber-200 px-4 py-3 text-sm text-[#1B3A5C]" role="status">
        <div className="max-w-7xl mx-auto flex flex-wrap items-center justify-between gap-2">
          <span>Customer preview · saved version{product.is_active ? '' : ' · hidden product'}. Purchasing is disabled.</span>
          <Link to={`/admin/products/${id}/edit`} className="underline font-semibold">Back to product editor</Link>
        </div>
      </div>}
      {/* Breadcrumb */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6 pb-2">
        <nav className="flex items-center text-sm text-gray-500" data-testid="product-breadcrumb">
          <Link to={isSchoolProduct ? "/back-to-school" : "/shop"} className="hover:text-[#4A90A4] transition-colors">{isSchoolProduct ? "Schools" : "Shop"}</Link>
          <ChevronRight className="w-4 h-4 mx-2" />
          {product.categories?.[0] && (
            <>
              <Link to={isSchoolProduct ? `/school/${product.categories[0].slug}` : `/shop?category=${product.categories[0].slug}`} className="hover:text-[#4A90A4] transition-colors">
                {product.categories[0].name}
              </Link>
              <ChevronRight className="w-4 h-4 mx-2" />
            </>
          )}
          <span className="text-gray-900 font-medium truncate">{product.name}</span>
        </nav>
      </div>

      {/* Product Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid md:grid-cols-2 gap-12">
          {/* Image Gallery */}
          <div>
            <div className="aspect-square rounded-2xl overflow-hidden bg-gray-50 mb-4" data-testid="product-main-image">
              {images[selectedImage]?.url ? (
                <img
                  src={images[selectedImage].url}
                  alt={images[selectedImage].alt_text || product.name}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <span className="text-7xl font-bold text-gray-200">{product.name.charAt(0)}</span>
                </div>
              )}
            </div>
            {images.length > 1 && (
              <div className="flex gap-3">
                {images.map((img, i) => (
                  <button
                    key={img.id || i}
                    onClick={() => setSelectedImage(i)}
                    className={`w-20 h-20 rounded-xl overflow-hidden border-2 transition-all ${
                      i === selectedImage ? 'border-[#4A90A4] ring-2 ring-[#4A90A4]/20' : 'border-gray-200 hover:border-gray-300'
                    }`}
                    data-testid={`product-thumb-${i}`}
                  >
                    {img.url ? (
                      <img src={img.url} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full bg-gray-100" />
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Product Info */}
          <div>
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-3" data-testid="product-title">
              {product.name}
            </h1>

            {/* Categories */}
            {product.categories?.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-4">
                {/* No /shop route exists; render as non-navigating pills until a catalog page is built */}
                {product.categories.map(cat => (
                  <Link to={`/shop?category=${cat.slug}`} key={cat.id}
                    className="text-xs font-medium px-3 py-1 rounded-full bg-[#4A90A4]/10 text-[#4A90A4]">
                    {cat.name}
                  </Link>
                ))}
              </div>
            )}

            {/* School context banner (from Back-to-School "Other Schools" flow) */}
            {schoolContext && (
              <div
                className="mb-5 flex items-start gap-3 rounded-xl px-4 py-3 border-l-4"
                style={{ backgroundColor: '#FFF7E5', borderColor: '#F5A623' }}
                data-testid="school-context-banner"
              >
                <GraduationCap className="w-5 h-5 mt-0.5 text-[#1B3A5C] flex-shrink-0" />
                <div className="text-sm">
                  <p className="font-semibold text-[#1B3A5C]">Logo application for {schoolContext}</p>
                  <p className="text-gray-600">Bring your own shirt — we&apos;ll apply your school logo in-store.</p>
                </div>
              </div>
            )}

            {/* Price */}
            <div className="flex items-baseline gap-3 mb-6" data-testid="product-price">
              <span className="text-3xl font-bold text-[#4A90A4]">${currentPrice().toFixed(2)}</span>
              {product.sale_price && (
                <span className="text-lg text-gray-400 line-through">${product.base_price.toFixed(2)}</span>
              )}
            </div>

            {/* Description */}
            <p className="text-gray-600 leading-relaxed mb-8" data-testid="product-description">
              {product.description}
            </p>

            {/* Variants — cascading Fit → Size → Color */}
            {!isBundle && product.variants?.length > 0 && (
              <VariantSelector
                variants={product.variants.filter(v => v.is_active)}
                selectedVariant={selectedVariant}
                onChange={setSelectedVariant}
              />
            )}

            {usesStructuredOptions && (
              <ProductOptionsPicker
                fits={productFits}
                sizes={productSizes}
                colors={productColors}
                selections={productSelections}
                onChange={setProductSelections}
              />
            )}

            {isCustom && !productColors.length && !selectedVariant && (
              <label className="block text-sm font-medium mb-5">Garment color preference
                <input className="block w-full min-h-[44px] mt-2 px-3 py-2 border border-gray-300 rounded-lg" maxLength={100}
                  value={productSelections.color || ''} onChange={e => setProductSelections({ ...productSelections, color: e.target.value })}
                  placeholder="Enter your preferred garment color" />
                <span className="block text-xs text-gray-500 mt-1">KYIS will confirm availability for colors not listed in the catalog.</span>
              </label>
            )}

            {/* Bundle — per-item fit, size, and color selection */}
            {isBundle && bundleSlots.length > 0 && (
              <BundleSlotsPicker
                slots={bundleSlots}
                getSel={getSlotSel}
                setSel={setSlotSel}
                totalQty={quantity}
              />
            )}

            {/* Size chart link — only on school products that have charts configured */}
            {sizingCharts.length > 0 && (
              <button
                type="button"
                onClick={() => setChartModalOpen(true)}
                className="inline-flex items-center gap-1.5 mb-6 text-sm font-medium text-[#4A90A4] hover:text-[#1B3A5C] underline-offset-4 hover:underline"
                data-testid="open-size-chart-btn"
              >
                <Ruler className="w-4 h-4" /> View size chart
              </button>
            )}

            {/* Customization — school products: outerwear only; always optional */}
            {showCustomization && !isCustom && (
              <div className="mb-6">
                <div className="flex items-baseline justify-between mb-2">
                  <label className="block text-sm font-semibold text-gray-900">
                    {product.name_personalization_available ? `Add a name (+$${product.name_personalization_fee.toFixed(2)} per item)` : 'Customization Text'}
                  </label>
                  <span className="text-xs font-medium text-gray-400 uppercase tracking-wider">Optional</span>
                </div>
                <textarea
                  value={customText}
                  onChange={(e) => setCustomText(e.target.value)}
                  aria-label="Personalization name"
                  placeholder={product.name_personalization_available ? "Enter the name to add" : "Enter customization details"}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#4A90A4] resize-none"
                  rows={3}
                  data-testid="customization-input"
                />
                <p className="mt-1.5 text-xs text-gray-500">Leave blank if no personalization is needed.</p>
              </div>
            )}

            {/* Quantity */}
            <div className="mb-8">
              <label className="block text-sm font-semibold text-gray-900 mb-3">Quantity</label>
              <div className="inline-flex items-center border-2 border-gray-200 rounded-xl overflow-hidden">
                <button
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  className="p-3 hover:bg-gray-50 transition-colors"
                  data-testid="quantity-decrease"
                >
                  <Minus className="w-4 h-4" />
                </button>
                <span className="px-6 py-3 min-w-[4rem] text-center font-semibold text-lg" data-testid="quantity-display">
                  {quantity}
                </span>
                <button
                  onClick={() => setQuantity(quantity + 1)}
                  className="p-3 hover:bg-gray-50 transition-colors"
                  data-testid="quantity-increase"
                >
                  <Plus className="w-4 h-4" />
                </button>
              </div>
            </div>

            {isCustom && <ProductCustomization preview={preview} token={token} product={product} value={decoration} onChange={setDecoration} />}
            {isCustom && <div className="my-4 text-sm" aria-live="polite">
              {customPrice ? <><p>Item price: ${customPrice.unit_price.toFixed(2)} × {quantity}</p><p>Setup: ${customPrice.options.pricing.setup_fee.toFixed(2)}</p><p className="font-semibold">Total before tax and shipping: ${customPrice.total_price.toFixed(2)}</p></> : <p>{priceError || 'Calculating your selections…'}</p>}
            </div>}
            {addError && <p role="alert" className="text-red-700 text-sm my-3">{addError}</p>}
            {/* Add to Cart */}
            <div className="flex gap-4">
              <button
                onClick={handleAddToCart}
                disabled={preview || cartLoading || (isCustom && !customPrice) || !productOptionsReady || (isBundle && !bundleReady)}
                className={`flex-1 flex items-center justify-center gap-3 px-8 py-4 rounded-full font-bold text-lg transition-all ${
                  addedToCart
                    ? 'bg-green-500 text-white'
                    : (!productOptionsReady || (isBundle && !bundleReady))
                    ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                    : 'bg-[#F5A623] text-gray-900 hover:shadow-lg hover:shadow-[#F5A623]/25'
                } disabled:opacity-50`}
                data-testid="add-to-cart-btn"
              >
                {addedToCart ? (
                  <>
                    <Check className="w-5 h-5" /> Added to Cart
                  </>
                ) : (!productOptionsReady) ? (
                  <>Pick required options</>
                ) : (isBundle && !bundleReady) ? (
                  <>Pick required options for every item</>
                ) : (
                  <>
                    <ShoppingCart className="w-5 h-5" /> Add to Cart — ${(isCustom && customPrice ? customPrice.total_price : currentPrice() * quantity).toFixed(2)}
                  </>
                )}
              </button>
            </div>

            {/* Stock info */}
            <p className="mt-4 text-sm text-gray-500" data-testid="stock-info">
              {product.stock_quantity > 10
                ? 'In Stock'
                : product.stock_quantity > 0
                ? `Only ${product.stock_quantity} left`
                : 'Made to Order'}
            </p>

            {/* Shipping note */}
            <div className="mt-6 p-4 bg-[#FDF6E9] rounded-xl border border-[#F5A623]/15">
              <p className="text-sm text-gray-700">
                <span className="font-semibold">Free shipping</span> on orders over $75.
                Standard turnaround: 5-7 business days.
              </p>
            </div>
          </div>
        </div>
      </div>
      {chartModalOpen && (
        <SizeChartModal charts={sizingCharts} onClose={() => setChartModalOpen(false)} />
      )}
    </div>
  );
};

export default ProductDetailPage;

// ── Product option picker (spreadsheet-backed fit/size/color arrays) ──
// Simple products imported from the spreadsheet store options directly on
// products.fits/products.sizes/products.colors instead of materializing every
// fit × size × color as a ProductVariant row.
const ProductOptionsPicker = ({ fits, sizes, colors, selections, onChange }) => {
  const setField = (field, value) => onChange((prev) => ({ ...prev, [field]: value }));
  return (
    <div className="mb-6 space-y-4" data-testid="product-options-picker">
      {fits.length > 0 && (
        <OptionSelect
          label="Fit"
          value={selections.fit || ''}
          values={fits}
          onChange={(value) => setField('fit', value)}
          testId="product-fit-select"
          formatter={titleCase}
        />
      )}
      {sizes.length > 0 && (
        <OptionSelect
          label="Size"
          value={selections.size || ''}
          values={[...sizes].sort(cmpSize)}
          onChange={(value) => setField('size', value)}
          testId="product-size-select"
        />
      )}
      {colors.length > 0 && (
        <OptionSelect
          label="Color"
          value={selections.color || ''}
          values={colors}
          onChange={(value) => setField('color', value)}
          testId="product-color-select"
          formatter={titleCase}
        />
      )}
    </div>
  );
};

const OptionSelect = ({ label, value, values, onChange, testId, formatter = (v) => v }) => (
  <div>
    <label className="block text-sm font-semibold text-gray-900 mb-2">{label}</label>
    <div className="relative">
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testId}
        className="w-full appearance-none px-4 py-3 pr-10 rounded-xl border-2 border-gray-200 bg-white text-sm font-medium text-gray-700 focus:outline-none focus:border-[#4A90A4] cursor-pointer transition-colors"
      >
        <option value="">Select {label.toLowerCase()}...</option>
        {values.map((val) => (
          <option key={val} value={val}>{formatter(val)}</option>
        ))}
      </select>
      <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
    </div>
  </div>
);

// ── Bundle slot picker (per-item fit/size/color) ────────────────────
// For school_uniform_bundle products, renders one row per bundle item ×
// quantity. Each row only asks for fields the component product actually has.
const BundleSlotsPicker = ({ slots, getSel, setSel, totalQty }) => {
  const showBundleTag = (totalQty || 1) > 1;
  return (
    <div className="mb-6 border border-[#4A90A4]/20 rounded-2xl p-5 bg-[#F7FBFC]" data-testid="bundle-slots-picker">
      <div className="flex items-baseline justify-between mb-4">
        <h3 className="text-sm font-bold uppercase tracking-wider text-[#1B3A5C]">
          Choose options for each item
        </h3>
        <span className="text-xs text-gray-500">{slots.length} item{slots.length === 1 ? '' : 's'}</span>
      </div>
      <div className="space-y-2.5">
        {slots.map((slot) => {
          const sel = getSel(slot.key);
          const fields = [
            slot.fits.length > 0 ? { key: 'fit', label: 'Fit', values: slot.fits, formatter: titleCase } : null,
            slot.sizes.length > 0 ? { key: 'size', label: 'Size', values: [...slot.sizes].sort(cmpSize) } : null,
            slot.colors.length > 0 ? { key: 'color', label: 'Color', values: slot.colors, formatter: titleCase } : null,
          ].filter(Boolean);
          return (
            <div key={slot.key}
              className="bg-white border border-gray-200 rounded-xl p-3.5"
              data-testid={`bundle-slot-${slot.key}`}>
              <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                <span className="text-sm font-semibold text-gray-900">
                  {slot.name}
                  {showBundleTag && (
                    <span className="ml-2 text-[10px] text-gray-400 uppercase tracking-wider font-bold">
                      Pack #{slot.bundle_index + 1}
                    </span>
                  )}
                </span>
              </div>
              {fields.length > 0 ? (
                <div className={`grid grid-cols-1 ${fields.length > 1 ? 'sm:grid-cols-2' : ''} gap-2.5`}>
                  {fields.map((field) => (
                    <div key={field.key}>
                      <label className="block text-[10px] uppercase font-bold tracking-wider text-gray-500 mb-1">{field.label}</label>
                      <select
                        value={sel[field.key] || ''}
                        onChange={(e) => setSel(slot.key, { [field.key]: e.target.value, product_id: slot.product_id })}
                        className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#4A90A4]/30"
                        data-testid={`bundle-slot-${slot.key}-${field.key}`}
                      >
                        <option value="">— Select {field.label.toLowerCase()} —</option>
                        {field.values.map((val) => (
                          <option key={val} value={val}>{field.formatter ? field.formatter(val) : val}</option>
                        ))}
                      </select>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-gray-500">No options required for this item.</p>
              )}
            </div>
          );
        })}
      </div>
      <p className="text-[11px] text-gray-500 mt-3">
        Each package item can have its own fit, size, and color when that data exists in the product spreadsheet.
      </p>
    </div>
  );
};

// ── Size chart modal ─────────────────────────────────────────────────
// Shows every active sizing chart for the school this product is tied to.
// Charts are grouped by fit and rendered either as <img> or as a <table>
// depending on chart_type. Fit tabs let customers jump between e.g.
// Adult / Youth / Ladies when a school has multiple charts.

const SizeChartModal = ({ charts, onClose }) => {
  const fits = Array.from(new Set(charts.map((c) => c.fit)));
  const [activeFit, setActiveFit] = React.useState(fits[0] || '');
  const visible = charts.filter((c) => c.fit === activeFit);

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose} data-testid="size-chart-modal">
      <div onClick={(e) => e.stopPropagation()}
        className="w-full max-w-3xl max-h-[90vh] bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <h2 className="text-base font-bold text-[#1B3A5C]">Size Chart</h2>
          <button onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600"
            data-testid="size-chart-close">
            <X className="w-5 h-5" />
          </button>
        </div>
        {fits.length > 1 && (
          <div className="flex border-b border-gray-100 overflow-x-auto px-5">
            {fits.map((f) => {
              const active = f === activeFit;
              return (
                <button key={f}
                  type="button"
                  onClick={() => setActiveFit(f)}
                  className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors whitespace-nowrap ${active ? 'border-[#F5A623] text-[#1B3A5C]' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
                  data-testid={`size-chart-tab-${f}`}>
                  {f.charAt(0).toUpperCase() + f.slice(1)}
                </button>
              );
            })}
          </div>
        )}
        <div className="flex-1 overflow-y-auto p-5 space-y-6">
          {visible.length === 0 ? (
            <p className="text-sm text-gray-400 text-center py-8">No chart for this fit.</p>
          ) : (
            visible.map((c) => (
              <div key={c.id}>
                <h3 className="text-sm font-bold text-[#1B3A5C] mb-2">{c.title}</h3>
                {c.chart_type === 'image' && c.image_url && (
                  <img src={c.image_url} alt={c.title}
                    className="w-full rounded-lg border border-gray-100" />
                )}
                {c.chart_type === 'table' && c.table_data && (
                  <div className="overflow-x-auto rounded-lg border border-gray-100">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 border-b border-gray-200">
                        <tr>
                          {(c.table_data.columns || []).map((col, i) => (
                            <th key={i} className="px-3 py-2 text-left text-xs font-bold uppercase tracking-wider text-gray-600">{col}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {(c.table_data.rows || []).map((row, ri) => (
                          <tr key={ri} className="border-b border-gray-50">
                            {row.map((cell, ci) => (
                              <td key={ci} className="px-3 py-2 text-gray-700">{cell}</td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                {c.notes && (
                  <p className="mt-2 text-xs text-gray-500 italic">{c.notes}</p>
                )}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};


// ── Cascading variant selector ───────────────────────────────────────
// Replaces a flat grid of {Fit/Size/Color} combination buttons with three
// rows: Fit → Size → Color. Each row only shows values that exist in the
// variants list, and pills get disabled when no remaining variant matches
// the choices so far. When all three axes are picked we look up the
// matching variant and bubble it back to the parent.

// Read options case-insensitively. Legacy data uses capitalized keys
// like "Gender/Age" / "Size" / "Color"; new data uses lowercase.
const getOpt = (v, axis) => {
  const o = v?.options || {};
  if (axis === 'fit') return o.fit || o.Fit || o['Gender/Age'] || '';
  if (axis === 'size') return o.size || o.Size || '';
  if (axis === 'color') return o.color || o.Color || '';
  return '';
};

const FIT_ORDER = ['youth', 'adult', 'ladies'];
const SIZE_ORDER = ['XS', 'S', 'M', 'L', 'XL', 'XXL', '2XL', '3XL', '4XL'];

const cmpFit = (a, b) => {
  const ai = FIT_ORDER.indexOf((a || '').toLowerCase());
  const bi = FIT_ORDER.indexOf((b || '').toLowerCase());
  if (ai >= 0 && bi >= 0) return ai - bi;
  if (ai >= 0) return -1;
  if (bi >= 0) return 1;
  return String(a).localeCompare(String(b));
};

const cmpSize = (a, b) => {
  const ai = SIZE_ORDER.indexOf((a || '').toUpperCase());
  const bi = SIZE_ORDER.indexOf((b || '').toUpperCase());
  if (ai >= 0 && bi >= 0) return ai - bi;
  if (ai >= 0) return -1;
  if (bi >= 0) return 1;
  return String(a).localeCompare(String(b));
};

const titleCase = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase() : '');

const VariantSelector = ({ variants, selectedVariant, onChange }) => {
  // Detect which axes are actually present in this product's variants.
  const hasFit = variants.some((v) => !!getOpt(v, 'fit'));
  const hasSize = variants.some((v) => !!getOpt(v, 'size'));
  const hasColor = variants.some((v) => !!getOpt(v, 'color'));

  // Bootstrap state from the currently selected variant (preserves the
  // parent's auto-pick of the first variant).
  const [fit, setFit] = React.useState(getOpt(selectedVariant, 'fit'));
  const [size, setSize] = React.useState(getOpt(selectedVariant, 'size'));
  const [color, setColor] = React.useState(getOpt(selectedVariant, 'color'));

  // Whenever fit/size/color form a valid combo, propagate the exact variant up.
  React.useEffect(() => {
    const match = variants.find((v) =>
      (!hasFit || getOpt(v, 'fit') === fit) &&
      (!hasSize || getOpt(v, 'size') === size) &&
      (!hasColor || getOpt(v, 'color') === color)
    );
    if (match && match.id !== selectedVariant?.id) onChange(match);
  }, [fit, size, color, variants, hasFit, hasSize, hasColor, selectedVariant, onChange]);

  // Helper: is there any variant satisfying these axis-filters?
  const exists = (filters) =>
    variants.some((v) =>
      Object.entries(filters).every(([axis, val]) => !val || getOpt(v, axis) === val)
    );

  // Compute available + enabled values per axis based on choices "above" it.
  const fitValues = hasFit
    ? Array.from(new Set(variants.map((v) => getOpt(v, 'fit')).filter(Boolean))).sort(cmpFit)
    : [];
  const sizeValues = hasSize
    ? Array.from(new Set(variants.map((v) => getOpt(v, 'size')).filter(Boolean))).sort(cmpSize)
    : [];
  const colorValues = hasColor
    ? Array.from(new Set(variants.map((v) => getOpt(v, 'color')).filter(Boolean))).sort((a, b) => a.localeCompare(b))
    : [];

  // When user picks a Fit that invalidates the current Size/Color, clear them.
  const pickFit = (v) => {
    setFit(v);
    if (size && !exists({ fit: v, size })) setSize('');
    if (color && !exists({ fit: v, size, color })) setColor('');
  };
  const pickSize = (v) => {
    setSize(v);
    if (color && !exists({ fit, size: v, color })) setColor('');
  };
  const pickColor = (v) => setColor(v);

  return (
    <div className="mb-6 space-y-4" data-testid="variant-selector">
      {hasFit && (
        <VariantDropdown label="Fit" axis="fit" values={fitValues}
          selected={fit} onPick={pickFit}
          prevFilters={{}} testidPrefix="vs-fit" exists={exists} />
      )}
      {hasSize && (
        <VariantDropdown label="Size" axis="size" values={sizeValues}
          selected={size} onPick={pickSize}
          prevFilters={{ fit }} testidPrefix="vs-size" exists={exists} />
      )}
      {hasColor && (
        <VariantDropdown label="Color" axis="color" values={colorValues}
          selected={color} onPick={pickColor}
          prevFilters={{ fit, size }} testidPrefix="vs-color" exists={exists} />
      )}
      {selectedVariant?.price_adjustment ? (
        <p className="text-xs text-gray-500">
          Price adjustment for this selection:&nbsp;
          {selectedVariant.price_adjustment > 0 ? '+' : ''}
          ${selectedVariant.price_adjustment.toFixed(2)}
        </p>
      ) : null}
    </div>
  );
};

const VariantDropdown = ({ label, axis, values, selected, onPick, prevFilters, testidPrefix, exists }) => {
  const formatVal = (v) => (axis === 'fit' || axis === 'color') ? titleCase(v) : v.toUpperCase();
  return (
    <div>
      <label className="block text-sm font-semibold text-gray-900 mb-2">{label}</label>
      <div className="relative">
        <select
          value={selected || ''}
          onChange={(e) => onPick(e.target.value)}
          data-testid={`${testidPrefix}-select`}
          className="w-full appearance-none px-4 py-3 pr-10 rounded-xl border-2 border-gray-200 bg-white text-sm font-medium text-gray-700 focus:outline-none focus:border-[#4A90A4] cursor-pointer transition-colors"
        >
          {!selected && <option value="" disabled>Select {label.toLowerCase()}...</option>}
          {values.map((val) => {
            const enabled = exists({ ...prevFilters, [axis]: val });
            return (
              <option key={val} value={val} disabled={!enabled}>
                {formatVal(val)}{!enabled ? ' (unavailable)' : ''}
              </option>
            );
          })}
        </select>
        <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
      </div>
    </div>
  );
};
